import { useEffect, useContext, useRef, useState } from "react";
import * as turf from "@turf/turf";
import { ReactComponent as DistanceIconSvg } from "assets/icon_distance_tool.svg";
import { ReactComponent as LineSvg } from "assets/line.svg";
import { ReactComponent as PolygonSvg } from "assets/polygon.svg";
import styles from "./distance-meter.module.scss";
import { MapContext } from "helpers/map";
import { useTranslation } from "react-i18next";
import { AppAction, AppContext } from "helpers/context";
import { DrawShapeSource, DrawShapeState } from "helpers/models";
import { Tooltip } from "@mui/material";
import c1 from "assets/json/c1.json";

type SpatialType = "GCJ02" | "WGS84";

function DistanceMeter() {
  const [t] = useTranslation();
  const [appContext, dispatch] = useContext(AppContext);
  const { AMap, map } = useContext(MapContext);

  const [mouseTool, setMouseTool] = useState<any>(null);
  const [type, setType] = useState("");

  const anchorsRef = useRef<Record<string, any[]>>({});
  const midAnchorsRef = useRef<Record<string, any[]>>({});
  const isEditingRef = useRef<boolean>(false);
  const draggingMidAnchorRef = useRef<any>(null);
  const popupRef = useRef<any>(null);
  const shapeDrawingRef = useRef<any[]>([]);
  const geodesicLineOptions = {
    geodesic: true
  };

  const chinaRings = (c1 as any)?.features?.[0]?.geometry?.rings || [];

  /**
   * 判断点是否在指定的环内（使用射线法）
   */
  const isPointInRing = (point: number[], ring: number[][]): boolean => {
    let inside = false;
    const x = point[0];
    const y = point[1];
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
      const xi = ring[i][0];
      const yi = ring[i][1];
      const xj = ring[j][0];
      const yj = ring[j][1];
      const intersect =
        yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
      if (intersect) inside = !inside;
    }
    return inside;
  };

  /**
   * 判断点是否在中国范围内
   */
  const isPointInChina = (point: number[]): boolean => {
    return chinaRings.some((ring: number[][]) => isPointInRing(point, ring));
  };

  /**
   * 根据坐标自动检测坐标系
   * 如果所有坐标都在中国境内，则为 GCJ02，否则为 WGS84
   */
  const detectSpatial = (coordinates: any[][]): SpatialType => {
    if (!coordinates || coordinates.length === 0) return "WGS84";
    const allInsideChina = coordinates.every((point) => isPointInChina(point));
    return allInsideChina ? "GCJ02" : "WGS84";
  };

  const isPolygonSource = (source?: DrawShapeSource) =>
    source === DrawShapeSource.POLYGON ||
    source === DrawShapeSource.SHAPE_POLYGON;

  const isPolygonMapObject = (shapeObj: any) => {
    if (!shapeObj) {
      return false;
    }

    const className =
      (shapeObj.CLASS_NAME as string) || (shapeObj.className as string) || "";

    if (className.includes("Polygon")) {
      return true;
    }

    if (className.includes("Polyline")) {
      return false;
    }

    if (AMap?.Polygon && shapeObj instanceof AMap.Polygon) {
      return true;
    }

    return false;
  };

  const getShapeById = (shapeId: string) =>
    appContext.shapes.find((p) => p.id === shapeId);

  const dedupeClosedPolygonPath = (path: any[]) => {
    if (!Array.isArray(path) || path.length < 2) {
      return path;
    }

    const firstPoint = path[0];
    const lastPoint = path[path.length - 1];
    const isClosedRing =
      firstPoint?.lng === lastPoint?.lng && firstPoint?.lat === lastPoint?.lat;

    return isClosedRing ? path.slice(0, -1) : path;
  };

  const getShapePath = (shapeObj: any, shapeId?: string) => {
    if (!shapeObj?.getPath) {
      return [];
    }

    const rawPath = shapeObj.getPath();
    if (!Array.isArray(rawPath)) {
      return [];
    }

    // AMap Polygon 可能返回 [ring1, ring2]，这里统一只编辑主环。
    const path = Array.isArray(rawPath[0]) ? rawPath[0] : rawPath;
    const shapeEntry = shapeId ? getShapeById(shapeId) : undefined;
    const isPolygon = shapeEntry
      ? isPolygonSource(shapeEntry.source)
      : isPolygonMapObject(shapeObj);

    if (!isPolygon) {
      return path;
    }

    // 多边形统一移除首尾重复点，避免编辑态多出一个零长度边的中点锚点。
    return dedupeClosedPolygonPath(path);
  };

  const setShapePath = (shapeObj: any, nextPath: any[]) => {
    const rawPath = shapeObj?.getPath?.();
    if (Array.isArray(rawPath) && Array.isArray(rawPath[0])) {
      shapeObj.setPath([nextPath]);
      return;
    }

    shapeObj?.setPath(nextPath);
  };

  const getPathCoordinates = (shapeObj: any, shapeId?: string) =>
    getShapePath(shapeObj, shapeId).map((p: any) => [p.lng, p.lat]);

  const buildPolygonFeature = (coordinates: Array<[number, number]>) => {
    const validCoordinates = coordinates.filter(
      (point) =>
        Array.isArray(point) &&
        point.length >= 2 &&
        Number.isFinite(point[0]) &&
        Number.isFinite(point[1])
    ) as Array<[number, number]>;

    if (validCoordinates.length < 3) {
      return undefined;
    }

    const closedRing =
      validCoordinates[0][0] ===
        validCoordinates[validCoordinates.length - 1][0] &&
      validCoordinates[0][1] ===
        validCoordinates[validCoordinates.length - 1][1]
        ? validCoordinates
        : [...validCoordinates, validCoordinates[0]];

    if (closedRing.length < 4) {
      return undefined;
    }

    try {
      return turf.polygon([closedRing]);
    } catch {
      return undefined;
    }
  };

  const getPolygonArea = (coordinates: Array<[number, number]>) => {
    const polygonFeature = buildPolygonFeature(coordinates);
    if (!polygonFeature) {
      return 0;
    }

    const area = turf.area(polygonFeature);

    return Number.isFinite(area) ? area : 0;
  };

  const isPolygonShapeById = (shapeId: string) =>
    isPolygonSource(getShapeById(shapeId)?.source);

  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const toDeg = (rad: number) => (rad * 180) / Math.PI;

  // 计算大圆弧中点，保证 geodesic 线段的虚拟锚点贴在线上。
  const getGreatCircleMidpoint = (
    p1: { lng: number; lat: number },
    p2: { lng: number; lat: number }
  ) => {
    const lat1 = toRad(p1.lat);
    const lon1 = toRad(p1.lng);
    const lat2 = toRad(p2.lat);
    const lon2 = toRad(p2.lng);

    const dLon = lon2 - lon1;
    const bx = Math.cos(lat2) * Math.cos(dLon);
    const by = Math.cos(lat2) * Math.sin(dLon);

    const lat3 = Math.atan2(
      Math.sin(lat1) + Math.sin(lat2),
      Math.sqrt((Math.cos(lat1) + bx) * (Math.cos(lat1) + bx) + by * by)
    );
    const lon3 = lon1 + Math.atan2(by, Math.cos(lat1) + bx);

    return new AMap.LngLat(toDeg(lon3), toDeg(lat3));
  };

  const getSegmentMidpoint = (
    p1: { lng: number; lat: number },
    p2: { lng: number; lat: number },
    isPolygon: boolean
  ) => {
    if (!isPolygon) {
      return getGreatCircleMidpoint(p1, p2);
    }

    // 面边在地图渲染上是投影平面直线，使用像素中点可避免经纬度均值带来的偏移。
    if (map?.lngLatToPixel && map?.pixelToLngLat) {
      const p1Px = map.lngLatToPixel(new AMap.LngLat(p1.lng, p1.lat));
      const p2Px = map.lngLatToPixel(new AMap.LngLat(p2.lng, p2.lat));
      const midPx = new AMap.Pixel(
        (p1Px.x + p2Px.x) / 2,
        (p1Px.y + p2Px.y) / 2
      );
      const midLngLat = map.pixelToLngLat(midPx);
      return new AMap.LngLat(midLngLat.lng, midLngLat.lat);
    }

    return new AMap.LngLat((p1.lng + p2.lng) / 2, (p1.lat + p2.lat) / 2);
  };

  const applyIdleStyle = (shape: any) => {
    if (!shape?.aMapRef) {
      return;
    }

    if (isPolygonSource(shape.source)) {
      shape.aMapRef.setOptions({
        strokeWeight: 2,
        strokeStyle: "solid",
        cursor: "pointer",
        draggable: false,
        fillOpacity: 0.2
      });
      return;
    }

    shape.aMapRef.setOptions({
      strokeWeight: 2,
      strokeStyle: "solid",
      cursor: "pointer",
      draggable: false,
      ...geodesicLineOptions
    });
  };

  const applySelectedStyle = (shape: any) => {
    if (!shape?.aMapRef) {
      return;
    }

    if (isPolygonSource(shape.source)) {
      shape.aMapRef.setOptions({
        strokeWeight: 3,
        strokeStyle: "solid",
        cursor: "pointer",
        fillOpacity: 0.3
      });
      return;
    }

    shape.aMapRef.setOptions({
      strokeWeight: 4,
      strokeStyle: "solid",
      ...geodesicLineOptions
    });
  };

  /* ---------------------- 创建锚点 ---------------------- */
  const createAnchors = (shapeObj: any, shapeId: string) => {
    const path = getShapePath(shapeObj, shapeId);
    const list: any[] = [];

    path.forEach((lnglat: any, idx: number) => {
      const m = new AMap.Marker({
        position: lnglat,
        draggable: true,
        offset: new AMap.Pixel(-6, -6),
        cursor: "pointer",
        content: `
          <div style="
            width:12px;
            height:12px;
            background:rgb(255,160,47);
            border-radius:50%;
            border:2px solid #fff;
            box-shadow:0 0 2px rgba(0,0,0,0.8);
          "></div>
        `
      });

      m.on("mouseover", (e: any) => {
        const pos = e.lnglat;
        if (!popupRef.current) {
          popupRef.current = new AMap.InfoWindow({
            isCustom: true,
            closeWhenClickMap: false,
            offset: new AMap.Pixel(0, -25),
            content: `
              <div style="
                background: rgba(0,0,0,0.8);
                color: #fff;
                padding: 6px 10px;
                border-radius: 4px;
                font-size: 12px;
                white-space: nowrap;
              ">
              ${t("map.dragToMove")}
              </div>
            `
          });
        }
        popupRef.current.open(map, pos);
        popupRef.current.isOpen = true;
      });

      m.on("mouseout", () => {
        if (popupRef.current) {
          popupRef.current.close();
          popupRef.current = null;
        }
      });

      m.on("dragging", (e: any) => {
        const pos = e.lnglat;
        const newPath = getPathCoordinates(shapeObj, shapeId);
        newPath[idx] = [pos.lng, pos.lat];
        setShapePath(shapeObj, newPath);

        // 更新统一的数据模型
        dispatch({
          type: AppAction.updateShapeCoordinates,
          payload: { id: shapeId, coordinates: newPath }
        });

        updateMidAnchors(newPath, shapeId);

        if (popupRef.current) {
          updatePopupDistance(newPath, isPolygonShapeById(shapeId));
          if (!popupRef.current.isOpen) {
            popupRef.current.open(map, pos);
            popupRef.current.isOpen = true;
          }
          popupRef.current.setPosition(pos);
        }
      });

      m.on("rightclick", () => {
        deleteNodeAt(shapeObj, shapeId, idx);
      });

      list.push(m);
    });

    anchorsRef.current[shapeId] = list;
  };

  const createMidAnchors = (shapeObj: any, shapeId: string) => {
    const path = getShapePath(shapeObj, shapeId);
    const isPolygon = isPolygonShapeById(shapeId);
    const list: any[] = [];

    const segmentCount = isPolygon ? path.length : path.length - 1;

    for (let i = 0; i < segmentCount; i++) {
      const p1 = path[i];
      const p2 = isPolygon ? path[(i + 1) % path.length] : path[i + 1];

      const mid = getSegmentMidpoint(p1, p2, isPolygon);

      const m = new AMap.Marker({
        position: mid,
        draggable: true,
        offset: new AMap.Pixel(-5, -5),
        cursor: "pointer",
        content: `
          <div style="
            width:10px;
            height:10px;
            background:rgb(255,160,47);
            border-radius:50%;
          "></div>
        `
      });

      let insertedIndex: any = null;

      m.on("dragstart", () => {
        m.setContent(`<div style="
            width:12px;
            height:12px;
            background:rgb(255,160,47);
            border-radius:50%;
            border:2px solid #fff;
            box-shadow:0 0 4px rgba(0,0,0,0.8);
            cursor:pointer;
          "></div>`);

        draggingMidAnchorRef.current = {
          marker: m,
          shapeId,
          insertIndex: i + 1
        };

        const newPath = getPathCoordinates(shapeObj, shapeId);
        const currentMid = m.getPosition();
        insertedIndex = i + 1;
        // 使用 marker 当前坐标，避免闭包中的旧 mid 坐标导致插点回跳。
        newPath.splice(insertedIndex, 0, [currentMid.lng, currentMid.lat]);
        setShapePath(shapeObj, newPath);

        dispatch({
          type: AppAction.updateShapeCoordinates,
          payload: { id: shapeId, coordinates: newPath }
        });

        if (!popupRef.current) {
          popupRef.current = new AMap.InfoWindow({
            isCustom: true,
            closeWhenClickMap: false,
            offset: new AMap.Pixel(0, -25),
            content: "<div></div>"
          });
          popupRef.current.open(map, m.getPosition());
          popupRef.current.isOpen = true;
        }
      });

      m.on("dragging", (e: any) => {
        const info = draggingMidAnchorRef.current;
        if (!info) return;

        const pos = e.lnglat;
        m.setPosition(pos);

        const path = getPathCoordinates(shapeObj, shapeId);
        path[info.insertIndex] = [pos.lng, pos.lat];
        setShapePath(shapeObj, path);

        dispatch({
          type: AppAction.updateShapeCoordinates,
          payload: { id: shapeId, coordinates: path }
        });

        if (popupRef.current) {
          updatePopupDistance(path, isPolygonShapeById(shapeId));
          if (!popupRef.current.isOpen) {
            popupRef.current.open(map, pos);
            popupRef.current.isOpen = true;
          }
          popupRef.current.setPosition(e.lnglat);
        }
      });

      m.on("dragend", () => {
        draggingMidAnchorRef.current = null;

        anchorsRef.current[shapeId]?.forEach((x) => map.remove(x));
        midAnchorsRef.current[shapeId]?.forEach((x) => map.remove(x));

        createAnchors(shapeObj, shapeId);
        createMidAnchors(shapeObj, shapeId);

        showAnchors(shapeId);
        showMidAnchors(shapeId);

        if (popupRef.current) {
          popupRef.current.close();
          popupRef.current = null;
        }
      });

      list.push(m);
    }

    midAnchorsRef.current[shapeId] = list;
  };

  /* ---------------------- 显示锚点 ---------------------- */
  const showAnchors = (shapeId: string) => {
    anchorsRef.current[shapeId]?.forEach((m) => map.add(m));
  };

  const showMidAnchors = (shapeId: string) => {
    midAnchorsRef.current[shapeId]?.forEach((m) => map.add(m));
  };

  /* ---------------------- 隐藏所有锚点 ---------------------- */
  const hideAllAnchors = () => {
    Object.values(anchorsRef.current).forEach((arr: any) =>
      arr?.forEach((m: any) => map.remove(m))
    );
    hideAllMidAnchors();
  };

  const hideAllMidAnchors = () => {
    Object.values(midAnchorsRef.current).forEach((arr: any) =>
      arr?.forEach((m: any) => map.remove(m))
    );
  };

  /* ---------------------- 删除节点 ---------------------- */
  const deleteNodeAt = (
    shapeObj: any,
    shapeId: string,
    deleteIndex: number
  ) => {
    const path = getPathCoordinates(shapeObj, shapeId);
    const minPathLength = isPolygonShapeById(shapeId) ? 3 : 2;

    if (path.length <= minPathLength) {
      return;
    }

    path.splice(deleteIndex, 1);

    setShapePath(shapeObj, path);
    dispatch({
      type: AppAction.updateShapeCoordinates,
      payload: { id: shapeId, coordinates: path }
    });

    anchorsRef.current[shapeId]?.forEach((m) => map.remove(m));
    midAnchorsRef.current[shapeId]?.forEach((m) => map.remove(m));

    createAnchors(shapeObj, shapeId);
    createMidAnchors(shapeObj, shapeId);

    showAnchors(shapeId);
    showMidAnchors(shapeId);
  };

  /* ---------------------- 重置所有形状 ---------------------- */
  const resetAll = () => {
    appContext.shapes.forEach((shapeEntry) => applyIdleStyle(shapeEntry));

    hideAllAnchors();
    isEditingRef.current = false;
  };

  /* ---------------------- 第一次点击：仅选中 ---------------------- */
  const highlight = (shapeId: string) => {
    resetAll();

    const selectedShapeEntry = appContext.shapes.find((p) => p.id === shapeId);
    applySelectedStyle(selectedShapeEntry);

    if (selectedShapeEntry) {
      dispatch({
        type: AppAction.setCurrentShape,
        payload: selectedShapeEntry
      });
    }

    dispatch({ type: AppAction.selectShape, payload: shapeId });
  };

  /* ---------------------- 进入编辑模式 ---------------------- */
  const enterEditMode = (shapeId: string) => {
    const shapeEntry = appContext.shapes.find((p) => p.id === shapeId);
    if (
      shapeEntry?.hasShapeAnalysed ||
      shapeEntry?.source === DrawShapeSource.SHAPE_LINE ||
      shapeEntry?.source === DrawShapeSource.SHAPE_POLYGON
    ) {
      return;
    }
    if (!shapeEntry?.aMapRef) return;

    const shapeObj = shapeEntry.aMapRef;

    if (isPolygonSource(shapeEntry.source)) {
      shapeObj.setOptions({
        draggable: true,
        cursor: "move",
        strokeStyle: "dashed",
        fillOpacity: 0.35
      });
    } else {
      shapeObj.setOptions({
        draggable: true,
        cursor: "move",
        strokeStyle: "dashed",
        ...geodesicLineOptions
      });
    }

    anchorsRef.current[shapeId]?.forEach((x) => map.remove(x));
    createAnchors(shapeObj, shapeId);
    showAnchors(shapeId);

    let dragStartLngLat: any = null;
    let originalPathLngLat: any = null;

    shapeObj.on("dragstart", (e: any) => {
      dragStartLngLat = e.lnglat;

      originalPathLngLat = anchorsRef.current[shapeId]?.map((m) => {
        const pos = m.getPosition();
        return [pos.lng, pos.lat];
      });

      if (!popupRef.current) {
        popupRef.current = new AMap.InfoWindow({
          isCustom: true,
          closeWhenClickMap: false,
          offset: new AMap.Pixel(0, -25),
          content: "<div></div>"
        });
        popupRef.current.open(map, dragStartLngLat);
        popupRef.current.isOpen = true;
      }
    });

    shapeObj.on("dragging", (e: any) => {
      const pos = e.lnglat;

      const lngOffset = pos.lng - dragStartLngLat.lng;
      const latOffset = pos.lat - dragStartLngLat.lat;

      const newPath = originalPathLngLat.map(([lng, lat]: any) => [
        lng + lngOffset,
        lat + latOffset
      ]);

      setShapePath(shapeObj, newPath);
      dispatch({
        type: AppAction.updateShapeCoordinates,
        payload: { id: shapeId, coordinates: newPath }
      });

      anchorsRef.current[shapeId]?.forEach((m, i) => {
        const p = newPath[i];
        m.setPosition(p);
      });

      updateMidAnchors(newPath, shapeId);

      if (popupRef.current) {
        updatePopupDistance(newPath, isPolygonShapeById(shapeId));
        if (!popupRef.current.isOpen) {
          popupRef.current.open(map, pos);
          popupRef.current.isOpen = true;
        }
        popupRef.current?.setPosition(pos);
      }
    });

    shapeObj.on("dragend", () => {
      const finalPath = anchorsRef.current[shapeId]?.map((m) => {
        const pos = m.getPosition();
        return [pos.lng, pos.lat];
      });

      setShapePath(shapeObj, finalPath);
      dispatch({
        type: AppAction.updateShapeCoordinates,
        payload: { id: shapeId, coordinates: finalPath }
      });

      // 统一重建一次虚拟锚点，避免高频拖拽后中点位置累计误差导致的视觉跳动。
      midAnchorsRef.current[shapeId]?.forEach((x) => map.remove(x));
      createMidAnchors(shapeObj, shapeId);
      showMidAnchors(shapeId);

      if (popupRef.current) {
        popupRef.current.close();
        popupRef.current = null;
      }
    });

    createMidAnchors(shapeObj, shapeId);
    showMidAnchors(shapeId);

    dispatch({ type: AppAction.enterShapeEditMode, payload: shapeId });
  };

  /* ---------------------- 虚拟锚点位置改变 ---------------------- */
  const updateMidAnchors = (path: any, shapeId: string) => {
    const mids = midAnchorsRef.current[shapeId];
    if (!mids) return;

    const isPolygon = isPolygonShapeById(shapeId);
    let normalizedPath = path;

    if (isPolygon) {
      const anchorPath = (anchorsRef.current[shapeId] || [])
        .map((marker: any) => {
          const position = marker?.getPosition?.();
          if (!position) {
            return undefined;
          }
          return [position.lng, position.lat];
        })
        .filter((point: any) => Array.isArray(point));

      if (anchorPath.length >= 3) {
        normalizedPath = anchorPath;
      }
    }

    const segmentCount = isPolygon
      ? normalizedPath.length
      : normalizedPath.length - 1;

    for (let i = 0; i < segmentCount; i++) {
      const p1 = normalizedPath[i];
      const p2 = isPolygon
        ? normalizedPath[(i + 1) % normalizedPath.length]
        : normalizedPath[i + 1];

      const mid = getSegmentMidpoint(
        { lng: p1[0], lat: p1[1] },
        { lng: p2[0], lat: p2[1] },
        isPolygon
      );

      const m = mids[i];
      m?.setPosition(mid);
    }
  };

  /* ---------------------- 计算路径长度并更新 popup 显示 ---------------------- */
  const updatePopupDistance = (path: any[], closed = false) => {
    if (!popupRef.current) return;

    if (closed && path.length >= 3) {
      const polygonPath = path.length > 2 ? [...path, path[0]] : path;
      const area = AMap?.GeometryUtil?.ringArea
        ? Math.abs(AMap.GeometryUtil.ringArea(polygonPath))
        : 0;

      let areaText = "";
      if (area < 1000000) {
        areaText = t("map.area(m2)", { data: area.toFixed(2) });
      } else {
        areaText = t("map.area(km2)", {
          data: (area / 1000000).toFixed(3)
        });
      }

      popupRef.current.setContent(`
            <div style="
              background: rgba(0,0,0,0.8);
              color: #fff;
              padding: 6px 10px;
              border-radius: 4px;
              font-size: 12px;
              white-space: nowrap;
            ">
             ${areaText}
            </div>
        `);
      return;
    }

    let total = 0;
    for (let i = 0; i < path.length - 1; i++) {
      total += AMap.GeometryUtil.distance(path[i], path[i + 1]);
    }

    let text = "";
    if (total < 1000) {
      text = t("map.length(m)", { data: total.toFixed(3) });
    } else {
      text = t("map.length(km)", { data: (total / 1000).toFixed(3) });
    }

    popupRef.current.setContent(`
            <div style="
              background: rgba(0,0,0,0.8);
              color: #fff;
              padding: 6px 10px;
              border-radius: 4px;
              font-size: 12px;
              white-space: nowrap;
            ">
             ${text}
            </div>
        `);
  };

  /* ---------------------- 开始绘制 ---------------------- */
  const startDraw = (drawType: string) => {
    if (popupRef.current) {
      popupRef.current.close();
      popupRef.current = null;
    }

    if (drawType === type) {
      mouseTool?.close(true);
      setType("");
      map.setDefaultCursor("");
      shapeDrawingRef.current = [];
      return;
    }

    map.setDefaultCursor("crosshair");
    hideAllAnchors();
    setType(drawType);
    shapeDrawingRef.current = [];

    popupRef.current = new AMap.InfoWindow({
      isCustom: true,
      closeWhenClickMap: false,
      offset: new window.AMap.Pixel(0, -20),
      content: `
            <div style="
              background: rgba(0,0,0,0.8);
              color: #fff;
              padding: 6px 10px;
              border-radius: 4px;
              font-size: 12px;
              white-space: nowrap;
            ">
              ${t("map.selectStartingPoint")}
            </div>
          `
    });

    const tool = new AMap.MouseTool(map);
    setMouseTool(tool);
    if (drawType === "polygon") {
      tool.polygon({
        strokeColor: "rgb(255,160,47)",
        strokeWeight: 2,
        strokeStyle: "dashed",
        fillColor: "rgb(255,160,47)",
        fillOpacity: 0.2
      });
    } else {
      tool.polyline({
        strokeColor: "rgb(255,160,47)",
        strokeWeight: 2,
        strokeStyle: "dashed",
        ...geodesicLineOptions
      });
    }
  };

  useEffect(() => {
    if (appContext.editingShapeId !== undefined) {
      return;
    }

    appContext.shapes.forEach((shapeEntry) => applyIdleStyle(shapeEntry));
    hideAllAnchors();
    isEditingRef.current = false;

    if (appContext.selectedShapeId) {
      const selectedShapeEntry = appContext.shapes.find(
        (shapeEntry) => shapeEntry.id === appContext.selectedShapeId
      );
      applySelectedStyle(selectedShapeEntry);
    }
  }, [
    appContext.shapes,
    appContext.selectedShapeId,
    appContext.editingShapeId
  ]);

  /* ---------------------- 绘制完成 ---------------------- */
  useEffect(() => {
    if (!mouseTool) return;

    const handleDraw = (e: any) => {
      resetAll();

      const shapeObj = e.obj;
      const isPolygon = isPolygonMapObject(shapeObj);
      const shapeId = crypto.randomUUID();

      if (isPolygon) {
        shapeObj.setOptions({
          strokeWeight: 3,
          strokeStyle: "solid",
          fillOpacity: 0.3,
          cursor: "pointer",
          bubble: true
        });
      } else {
        shapeObj.setOptions({
          strokeWeight: 4,
          strokeStyle: "solid",
          cursor: "pointer",
          bubble: true,
          ...geodesicLineOptions
        });
      }

      const coordinates = getPathCoordinates(shapeObj);

      if (isPolygon) {
        const rollbackInvalidPolygon = (title: string, message: string) => {
          if (shapeObj && map) {
            map.remove(shapeObj);
          }

          dispatch({
            type: AppAction.addSnackBarParams,
            payload: {
              titleIcon: "error",
              titleIconColor: "red",
              title,
              message
            }
          });

          mouseTool.close();
          setType("");
          map.setDefaultCursor("");
          shapeDrawingRef.current = [];
          if (popupRef.current) {
            popupRef.current.close();
            popupRef.current = null;
          }
        };

        const area = getPolygonArea(coordinates as Array<[number, number]>);

        if (area <= 0) {
          rollbackInvalidPolygon(
            "polygonAreaZeroTitle",
            "polygonAreaZeroMessage"
          );
          return;
        }
      }

      // 将新的 shape 添加到统一模型
      const detectedSpatial = detectSpatial(coordinates);
      const newShapeData = {
        id: shapeId,
        source: isPolygon ? DrawShapeSource.POLYGON : DrawShapeSource.LINE,
        name: `${t(isPolygon ? "drawShape.polygonInfoTitle" : "drawShape.lineInfoTitle", { data: appContext.shapes.length + 1 })}`,

        type: isPolygon ? "Region" : "Ground",
        coordinates,
        state: "selected" as DrawShapeState,
        aMapRef: shapeObj,
        spatial: detectedSpatial
      };

      const nextShapes = [...appContext.shapes, newShapeData];
      dispatch({ type: AppAction.setShapes, payload: nextShapes });
      dispatch({ type: AppAction.selectShape, payload: shapeId });

      createAnchors(shapeObj, shapeId);

      isEditingRef.current = false;

      mouseTool.close();
      setType("");
      map.setDefaultCursor("");
      shapeDrawingRef.current = [];
      if (popupRef.current) {
        popupRef.current.close();
        popupRef.current = null;
      }
    };

    mouseTool.on("draw", handleDraw);

    return () => mouseTool.off("draw", handleDraw);
  }, [mouseTool, appContext.shapes]);

  /* ---------------------- 地图点击、划入划出 ---------------------- */
  useEffect(() => {
    if (!map) return;

    const handleClick = (e: any) => {
      const lnglat = e.lnglat;

      // 如果正在绘制，处理绘制逻辑
      if (type) {
        if (popupRef.current) {
          if (!shapeDrawingRef.current.length) {
            popupRef.current.close();
            popupRef.current.isOpen = false;
          }
          shapeDrawingRef.current.push(lnglat);
        }
        return;
      }

      let hitShapeId: string | null = null;

      // 检查点击的是否命中某个 shape
      appContext.shapes.forEach((shapeEntry) => {
        const aMapShapeObj = shapeEntry.aMapRef;
        if (!aMapShapeObj?.contains) {
          return;
        }

        // myshape 线可能已从地图移除但仍保留在状态里，此时 contains 会抛错
        const shapeMap = aMapShapeObj.getMap?.();
        if (!shapeMap) {
          return;
        }

        try {
          if (aMapShapeObj.contains(lnglat)) {
            hitShapeId = shapeEntry.id;
          }
        } catch {
          // 忽略单条异常，保证地图点击链路继续执行
        }
      });

      if (hitShapeId) {
        const currentSelected = appContext.selectedShapeId;
        const hitShape = appContext.shapes.find(
          (shapeEntry) => shapeEntry.id === hitShapeId
        );

        // 第2次点击 → 编辑模式
        if (
          currentSelected === hitShapeId &&
          !isEditingRef.current &&
          !hitShape?.hasShapeAnalysed &&
          hitShape?.source !== DrawShapeSource.SHAPE_LINE &&
          hitShape?.source !== DrawShapeSource.SHAPE_POLYGON
        ) {
          enterEditMode(hitShapeId);
          isEditingRef.current = true;
          return;
        }

        // 第1次点击 → 选中
        highlight(hitShapeId);
        isEditingRef.current = false;
      } else if (appContext.editingShapeId || appContext.selectedShapeId) {
        resetAll();
        dispatch({ type: AppAction.deselectShape });
      } else {
        // 地图点击查询地址
        dispatch({ type: AppAction.setMapClickEvent, payload: e });
      }
    };

    const handleMouseover = () => {
      if (popupRef.current && !popupRef.current.isOpen) {
        let center = map.getCenter();
        popupRef.current.open(map, center);
        popupRef.current.isOpen = true;
      }
    };

    const handleMousemove = (e: any) => {
      if (shapeDrawingRef.current.length && popupRef.current) {
        const tempArr = [...shapeDrawingRef.current, e.lnglat];
        updatePopupDistance(tempArr, type === "polygon");
        if (!popupRef.current.isOpen) {
          popupRef.current.open(map, e.lnglat);
          popupRef.current.isOpen = true;
        }
      }
      if (popupRef.current?.isOpen) {
        popupRef.current.setPosition(e.lnglat);
      }
    };

    const handleMouseout = () => {
      if (popupRef.current) {
        popupRef.current.close();
        popupRef.current.isOpen = false;
      }
    };

    map.on("click", handleClick);
    map.on("mouseover", handleMouseover);
    map.on("mousemove", handleMousemove);
    map.on("mouseout", handleMouseout);

    return () => {
      map.off("click", handleClick);
      map.off("mouseover", handleMouseover);
      map.off("mousemove", handleMousemove);
      map.off("mouseout", handleMouseout);
    };
  }, [
    map,
    type,
    appContext.shapes,
    appContext.selectedShapeId,
    appContext.editingShapeId
  ]);

  return (
    <div className={`dropdown dropdown-left ${styles.distance}`}>
      <div
        tabIndex={0}
        role="button"
        className="w-[36px] h-[36px] flex items-center justify-center cursor-pointer"
      >
        <Tooltip title={t("draw.DrawingTools")}>
          <div className="flex items-center justify-center cursor-pointer">
            <DistanceIconSvg
              className={`${type ? styles.iconActive : styles.icon} w-[36px] h-[36px] p-[7px]`}
            />
          </div>
        </Tooltip>
      </div>

      <div
        className={`${styles.draw} dropdown-content bg-base-100 shadow`}
        tabIndex={0}
      >
        <Tooltip title={t("draw.polygon")}>
          <PolygonSvg
            className={`${type === "polygon" ? styles.iconActive : styles.icon}`}
            style={{
              width: "20px",
              height: "20px"
            }}
            onClick={() => startDraw("polygon")}
          />
        </Tooltip>
        <Tooltip title={t("draw.line")}>
          <LineSvg
            className={`${type === "line" ? styles.iconActive : styles.icon}`}
            style={{
              width: "18px",
              height: "18px",
              marginTop: "2px"
            }}
            onClick={() => startDraw("line")}
          />
        </Tooltip>
      </div>
    </div>
  );
}

export default DistanceMeter;
